import React, { useState } from 'react'
import { useLanguage } from '@/components/LanguageContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { MapPin, Phone, Mail, Send, ChevronDown, ChevronUp, MessageCircle, Clock, Users, Heart, Globe, Shield, BookOpen, Target, Lightbulb } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast } from '@/hooks/use-toast'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'

interface ContactFormData {
  name: string
  email: string
  message: string
}

interface FAQItem {
  id: number
  question: string
  answer: string
  icon: React.ReactNode
}

const faqData: FAQItem[] = [
  {
    id: 1,
    question: "How can I volunteer with VJN?",
    answer: "You can apply to volunteer through our Careers page or contact us directly. We welcome both local and international volunteers with various skills and backgrounds. We offer opportunities in education, sports, arts, peacebuilding, and community development.",
    icon: <Users className="h-5 w-5 text-blue-600" />
  },
  {
    id: 2,
    question: "How is my donation used?",
    answer: "Your donations directly support our programs for youth empowerment, including education initiatives, entrepreneurship training, peace-building activities, and community development projects. We maintain full transparency in our financial reporting.",
    icon: <Heart className="h-5 w-5 text-red-600" />
  },
  {
    id: 3,
    question: "Can my organization partner with VJN?",
    answer: "Yes, we welcome partnerships with organizations that share our mission. We collaborate with NGOs, government agencies, educational institutions, and private companies. Please contact us with your partnership proposal.",
    icon: <Globe className="h-5 w-5 text-green-600" />
  },
  {
    id: 4,
    question: "Where do your programs operate?",
    answer: "Our programs operate primarily in the Rubavu District, with our headquarters in Gisenyi. We also collaborate with partners across Rwanda and have extended our reach to refugee camps and other districts through partnerships.",
    icon: <MapPin className="h-5 w-5 text-purple-600" />
  },
  {
    id: 5,
    question: "What age groups do you serve?",
    answer: "We primarily serve youth aged 15-35, but our programs are inclusive and we work with people of all ages. Our focus is on empowering young people to become leaders and change-makers in their communities.",
    icon: <Target className="h-5 w-5 text-orange-600" />
  },
  {
    id: 6,
    question: "How can I apply for VJN programs?",
    answer: "You can apply for our programs through our website, by visiting our office, or by contacting us directly. We have different application processes for different programs, and our team will guide you through the process.",
    icon: <BookOpen className="h-5 w-5 text-indigo-600" />
  },
  {
    id: 7,
    question: "What makes VJN different from other organizations?",
    answer: "VJN combines education, sports, arts, and peacebuilding in a unique way. We focus on holistic youth development, community engagement, and sustainable impact. Our 20+ years of experience and strong community relationships set us apart.",
    icon: <Lightbulb className="h-5 w-5 text-yellow-600" />
  },
  {
    id: 8,
    question: "How can I stay updated on VJN activities?",
    answer: "Follow us on social media (@visionjeunesse2), subscribe to our newsletter, or visit our website regularly. We also share updates through our community partners and local media outlets.",
    icon: <MessageCircle className="h-5 w-5 text-cyan-600" />
  },
  {
    id: 9,
    question: "What are VJN's main achievements?",
    answer: "Over 20 years, we've trained 2,400+ youth in vocational skills, supported 300+ youth groups, helped 10,000+ refugee youth, transformed 10,000+ teen mothers' lives, and operated in 5 refugee camps. We've also received recognition from the Government of Rwanda.",
    icon: <Shield className="h-5 w-5 text-emerald-600" />
  },
  {
    id: 10,
    question: "How can I contact VJN for urgent matters?",
    answer: "For urgent matters, call us at +250 785 403 435 or +250 788 892 826. You can also email us at visionjeunesse2050@gmail.com or visit our office in Gisenyi, Rubavu District. We respond to urgent inquiries within 24 hours.",
    icon: <Clock className="h-5 w-5 text-rose-600" />
  }
]

const Contact = () => {
  const { t } = useLanguage()
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<ContactFormData>()
  const { t: tTranslation } = useTranslation()
  const [openFAQ, setOpenFAQ] = useState<number | null>(null)

  const onSubmit = async (data: ContactFormData) => {
    try {
      console.log('Form submitted:', data)
      await new Promise(resolve => setTimeout(resolve, 1000))
      toast({
        title: t('contact.form.success.title', 'Message sent!'),
        description: t('contact.form.success.description', "We'll get back to you as soon as possible.")
      })
      reset()
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('contact.form.error.title', 'Error'),
        description: t('contact.form.error.description', 'There was a problem with your submission.')
      })
    }
  }

  const toggleFAQ = (id: number) => {
    setOpenFAQ(openFAQ === id ? null : id)
  }

  return (
    <div className="min-h-screen">
      <section className="relative bg-vjn-blue text-white py-20">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-green-600">
              {tTranslation('contact.heroTitle', 'Get in Touch')}
            </h1>
            <p className="text-lg md:text-xl max-w-2xl mx-auto">
              {tTranslation('contact.heroSubtitle', "Have questions? We're here to help. Reach out to us and be part of our mission")}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
            >
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full mb-4">
                  <MessageCircle className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {t('contact.form.title', 'Send us a message')}
                </h2>
                <p className="text-gray-600">
                  We'd love to hear from you. Send us a message and we'll respond as soon as possible.
                </p>
              </div>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('contact.form.name', 'Your Name')} *
                    </label>
                    <Input
                      id="name"
                      placeholder={t('contact.form.namePlaceholder', 'Enter your name')}
                      {...register('name', { required: t('contact.form.error.nameRequired', 'Name is required') })}
                      className={`h-12 px-4 border-2 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    />
                    {errors.name && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <span className="mr-1">⚠️</span>
                        {errors.name.message}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('contact.form.email', 'Your Email')} *
                    </label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('contact.form.emailPlaceholder', 'Enter your email')}
                      {...register('email', { 
                        required: t('contact.form.error.emailRequired', 'Email is required'),
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: t('contact.form.error.emailInvalid', 'Invalid email address')
                        }
                      })}
                      className={`h-12 px-4 border-2 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    />
                    {errors.email && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <span className="mr-1">⚠️</span>
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('contact.form.message', 'Message')} *
                  </label>
                  <Textarea
                    id="message"
                    placeholder={t('contact.form.messagePlaceholder', 'Tell us about your inquiry, suggestion, or how we can help you...')}
                    rows={6}
                    {...register('message', { required: t('contact.form.error.messageRequired', 'Message is required') })}
                    className={`px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                      errors.message ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  />
                  {errors.message && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <span className="mr-1">⚠️</span>
                      {errors.message.message}
                    </p>
                  )}
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t('contact.form.sending', 'Sending...')}
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Send className="h-5 w-5 mr-2" />
                      {t('contact.form.submit', 'Send Message')}
                    </div>
                  )}
                </Button>
              </form>
            </motion.div>
            
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {t('contact.info.title', 'Contact Information')}
                </h2>
                <p className="text-gray-600">
                  Get in touch with us through any of these channels
                </p>
              </div>
              
              <div className="space-y-6">
                {/* Address */}
                <motion.div 
                  className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-start">
                    <div className="mr-4 bg-gradient-to-r from-blue-500 to-blue-600 p-3 rounded-full">
                      <MapPin className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-gray-900">
                        {t('contact.info.address', 'Address')}
                      </h3>
                      <div className="text-gray-600 space-y-1">
                        <p className="font-medium">Vision Jeunesse Nouvelle (VJN)</p>
                        <p>{t('contact.info.addressLine1', 'Rubavu District, Gisenyi Sector')}</p>
                        <p>{t('contact.info.addressLine2', 'Nengo Cell, Gikarani Village')}</p>
                        <p>{t('contact.info.country', 'Rwanda')}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
                
                {/* Phone */}
                <motion.div 
                  className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-start">
                    <div className="mr-4 bg-gradient-to-r from-green-500 to-green-600 p-3 rounded-full">
                      <Phone className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-gray-900">
                        {t('contact.info.phone', 'Phone')}
                      </h3>
                      <div className="text-gray-600 space-y-1">
                        <p className="font-medium">+250 785 403 435</p>
                        <p className="font-medium">+250 788 892 826</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
                
                {/* Email */}
                <motion.div 
                  className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-start">
                    <div className="mr-4 bg-gradient-to-r from-purple-500 to-purple-600 p-3 rounded-full">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-gray-900">
                        {t('contact.info.email', 'Email')}
                      </h3>
                      <div className="text-gray-600">
                        <p className="font-medium">visionjeunesse2050@gmail.com</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
              
              {/* Google Map Embed */}
              <motion.div 
                className="mt-8 rounded-2xl overflow-hidden shadow-xl border border-gray-200"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3987.5087454351392!2d29.25718731475084!3d-1.7022109986683214!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x19dd050a47fb11e7%3A0xe550726afa8eb90a!2sVision%20Jeunesse%20Nouvelle!5e0!3m2!1sen!2srw!4v1647887291012!5m2!1sen!2srw"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen={true}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={t('contact.info.mapTitle', 'Vision Jeunesse Nouvelle Location Map')}
                />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-blue-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              {t('contact.faq.title', 'Frequently Asked Questions')}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('contact.faq.subtitle', 'Find answers to common questions about our organization and programs.')}
            </p>
          </motion.div>
          
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <motion.div
                key={faq.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      {faq.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                  </div>
                  <div className="flex-shrink-0">
                    {openFAQ === faq.id ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </div>
                </button>
                
                <AnimatePresence>
                  {openFAQ === faq.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 pt-2">
                        <div className="pl-9">
                          <p className="text-gray-700 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact
